import {
  IconBricksFilled,
  IconClipboardSignature,
  IconCopyFilled,
  IconEyeFilled,
  IconFileWithItemsFilled,
  IconPound,
  IconStarFilled,
  IconPartyPopper,
} from '@iwoca/icons';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useIntroducerData } from 'contexts/IntroducerData/IntroducerData.context';
import { useLocale } from 'hooks/useLocale';
import { Link } from 'react-router-dom';
import { TFAQsData } from 'types';
import { PATH_NAMES } from 'utils/routes/pathNames';
import styles from './FAQs.module.css';

type TApplicationStatus = {
  value: string;
  description: string;
  showInDE: boolean;
};

export const applicationStatusData = (): TApplicationStatus[] => [
  {
    value: t`Applicant funding steps`,
    description: t`The applicant is going through our funding steps. These steps include things like completing an ID check and signing their loan agreement. You can keep track of their progress through our live tracker.`,
    showInDE: true,
  },
  {
    value: t`Application declined`,
    description: t`The application has been declined. Where possible, we’ll send details to you by email on why we’ve made this decision.`,
    showInDE: true,
  },
  {
    value: t`Application deferred`,
    description: t`We’re currently not able to proceed with this application.`,
    showInDE: true,
  },
  {
    value: t`Awaiting underwriter`,
    description: t`The application is complete and an underwriter will be assigned to review it soon.`,
    showInDE: true,
  },
  {
    value: t`Continue app`,
    description: t`We've saved your progress in this application, so you can complete it later. You'll need to provide additional information in order to progress it.`,
    showInDE: true,
  },
  {
    value: t`Docs required`,
    description: t`We need some additional documentation, such as bank statements or filed company accounts, to complete this application.`,
    showInDE: true,
  },
  {
    value: t`Funded`,
    description: t`The applicant has successfully drawn down funds.`,
    showInDE: true,
  },
  {
    value: t`In review by underwriter`,
    description: t`An underwriter is reviewing the application - we’ll send you an update about it soon.`,
    showInDE: true,
  },
  {
    value: t`Manual check`,
    description: t`Our team needs to manually check details in the application before it can be reviewed. This could be for a number of reasons, but we'll normally complete these checks in a couple of hours and will let you know if there are any issues.`,
    showInDE: true,
  },
  {
    value: t`Offer expired`,
    description: t`The offer for this application has expired.`,
    showInDE: true,
  },
  {
    value: t`Offer rejected`,
    description: t`iwoca’s offer was rejected either by the applicant or by yourself on their behalf.`,
    showInDE: true,
  },
  {
    value: t`Offer revoked`,
    description: t`We’ve withdrawn the offer for this applicant.`,
    showInDE: true,
  },
  {
    value: t`Reopen app`,
    description: t`The original offer has expired, but you can resubmit the original application to review the applicant for an updated decision. Additional documents may be required.`,
    showInDE: true,
  },
  {
    value: t`Review conditional offer`,
    description: t`The application has been approved, subject to certain conditions being met. You’re able to review the offer(s) available to the applicant and we’ll email you details of what’s needed to fulfil the conditions. We reserve the right to revoke this offer.`,
    showInDE: false,
  },
  {
    value: t`Review offer`,
    description: t`The application has been approved and you're able to review the offer(s) available to the applicant.`,
    showInDE: true,
  },
];

const renderProductsButton = (label = 'Products') => (
  <Link to={PATH_NAMES.products()} target="_blank" rel="noopener noreferrer">
    {label}
  </Link>
);

export const FAQsData = (): TFAQsData[] => {
  const { isDE, isUK } = useLocale();
  const { splitTests } = useIntroducerData();
  const descriptionsToDisplay = isDE
    ? applicationStatusData()?.filter((desc) => desc.showInDE)
    : applicationStatusData();

  const data = [
    {
      id: 'application-process',
      icon: <IconClipboardSignature />,
      topic: t`Application process`,
      questions: [
        {
          id: `I've completed my application, what happens next?`,
          question: (
            <Trans>I've completed my application, what happens next?</Trans>
          ),
          answer: (
            <p>
              <Trans>
                Your application will be reviewed by our underwriting team once
                you have completed the application and supplied documents where
                required. If approved, full offer details will be shared with
                you via email as well as being available within the portal.
              </Trans>
            </p>
          ),
          showInDE: true,
        },
        {
          id: 'Why has my application been automatically rejected?',
          question: (
            <Trans>Why has my application been automatically rejected?</Trans>
          ),
          answer: (
            <p>
              <Trans>
                For auto applications a decline typically relates to concerns
                over personal or business credit. For manual applications our
                team will be able to advise.
              </Trans>
            </p>
          ),
          showInDE: true,
        },
        {
          id: 'What does each application status mean?',
          question: <Trans>What does each application status mean?</Trans>,
          answer: (
            <>
              {descriptionsToDisplay.map((applicationStatus) => {
                return (
                  <div className={styles.status} key={applicationStatus.value}>
                    <h4>{applicationStatus.value}</h4>
                    <p>{applicationStatus.description}</p>
                  </div>
                );
              })}
            </>
          ),
          showInDE: true,
        },
        {
          id: 'How do top ups work?', // Kept this id the same to keep the Amplitude event data unified
          question: <Trans>How does requesting more funds work?</Trans>,
          answer: (
            <p>
              <Trans>
                If a customer you've previously introduced to us is seeking
                further funding, they can do so by contacting their iwoca
                account manager directly. Alternatively you can re-refer your
                client via the portal.
              </Trans>
            </p>
          ),
          showInDE: true,
        },
        {
          id: 'How quickly can customers receive their funds?',
          question: (
            <Trans>How quickly can customers receive their funds?</Trans>
          ),
          answer: (
            <p>
              <Trans>
                Once you've accepted the offer and your customer has completed
                our funding steps, funds will typically be deposited in their
                bank account within minutes.
              </Trans>
            </p>
          ),
          showInDE: true,
        },
        {
          id: 'What is a conditional offer?',
          question: 'What is a conditional offer?',
          answer: (
            <p>
              If we are unsure about a businesses' VAT position, we may make an
              offer which is subject to us receiving copies of their VAT
              returns. Once received, the offer will be confirmed or may be
              revoked.
            </p>
          ),
          showInDE: false,
        },
        {
          id: 'How do I accept or decline an offer?',
          question: <Trans>How do I accept or decline an offer?</Trans>,
          answer: (
            <p>
              <Trans>
                Navigate to the <strong>Review offer</strong> tab of any
                approved application and tell us whether the customer accepts or
                rejects our offer. If proceeding, we'll ask you to confirm their
                contact details and a suitable time to call. For offers not
                going ahead, select <strong>Reject this offer</strong> and let
                us know why - it helps us to improve!
              </Trans>
            </p>
          ),
          showInDE: true,
        },
      ],
    },
    {
      id: 'requirements',
      icon: <IconFileWithItemsFilled />,
      topic: t`Requirements`,
      questions: [
        {
          id: 'What information do you typically require for an application?',
          question: (
            <Trans>
              What information do you typically require for an application?
            </Trans>
          ),
          answer: (
            <p>
              <Trans>
                We require basic information regarding the guarantor of the
                loan, as well as the business - all of which can be submitted in
                a few minutes. A list of our document requirements can also be
                found on our individual
              </Trans>{' '}
              {renderProductsButton('product guides')}.
            </p>
          ),
          showInDE: true,
        },
        {
          id: 'Can my customer link open banking?',
          question: 'Can my customer link open banking?',
          answer: (
            <p>
              Yes, customers can link Open Banking with us via TrueLayer. We
              also accept PDF bank statements that have been downloaded from
              Online Banking.
            </p>
          ),
          showInDE: false,
        },
        {
          id: 'Can I add more than one guarantor to an application?',
          question: (
            <Trans>Can I add more than one guarantor to an application?</Trans>
          ),
          answer: (
            <p>
              <Trans>
                Yes you can add as many guarantors as you would like, as long as
                one guarantor is an existing director within the business.
              </Trans>
            </p>
          ),
          showInDE: true,
        },
        {
          id: 'What businesses can iwoca help?',
          question: <Trans>What businesses can iwoca help?</Trans>,
          answer: (
            <p>
              <Trans>
                We can consider all Limited Companies across a range of sectors,
                from hospitality to construction.
              </Trans>
            </p>
          ),
          showInDE: true,
        },
        {
          id: 'How do I provide additional documents?',
          question: <Trans>How do I provide additional documents?</Trans>,
          answer: (
            <p>
              <Trans>
                If you have an open application with us, simply navigate to the{' '}
                <strong>Documents</strong> tab of your chosen application, where
                you'll then be able to drag & drop supporting documentation.
              </Trans>
            </p>
          ),
          showInDE: true,
        },
        {
          id: 'How can I invite additional users?',
          question: <Trans>How can I invite additional users?</Trans>,
          answer: (
            <p>
              <Trans>
                You can invite colleagues to create their own account by
                clicking the <strong>Invite</strong> button in the navigation
                bar, and sending them your companies unique referral link.
              </Trans>
            </p>
          ),
          showInDE: true,
        },
      ],
    },
    {
      id: 'our-products',
      icon: <IconCopyFilled />,
      topic: t`Our products`,
      questions: [
        {
          id: `My products aren't enabled, why?`,
          question: `My products aren't enabled, why?`,
          answer: (
            <p>
              In order to apply for any of our products, you'll first need to
              review and agree to our terms & conditions. You can request these
              by clicking on {renderProductsButton()} in the navigation bar.
            </p>
          ),
          showInDE: false,
        },
        {
          id: 'Where can I learn more about your products?',
          question: <Trans>Where can I learn more about your products?</Trans>,
          answer: (
            <p>
              <Trans>
                You can learn about iwoca's available products by downloading
                the available guides on our
              </Trans>{' '}
              {renderProductsButton()}{' '}
              <Trans>page, or alternatively just give us a call.</Trans>
            </p>
          ),
          showInDE: true,
        },
      ],
    },
    {
      id: 'your-commission',
      icon: <IconStarFilled />,
      topic: t`Your commission`,
      questions: [
        {
          id: 'How is my commission calculated?',
          question: <Trans>How is my commission calculated?</Trans>,
          answer: (
            <p>
              <Trans>
                Your rate of commission is determined by loan product, as well
                as whether documents have been supplied as part of our review.
                If you have an offer and you'd like to discuss your commission
                in detail, please give us a call.
              </Trans>
            </p>
          ),
          showInDE: true,
        },
        {
          id: 'How do I get paid my commission?',
          question: <Trans>How do I get paid my commission?</Trans>,
          answer: (
            <p>
              <Trans>
                We will calculate and pay any due commission due to you
                directly, typically within a few days of the customer being
                funded. We don't require any invoices, unless specifically
                requested.
              </Trans>
            </p>
          ),
          showInDE: true,
        },
      ],
    },
  ];

  if (splitTests?.commission_aware_pricing_enabled) {
    data.push({
      id: 'new-commission-structure',
      icon: <IconBricksFilled />,
      topic: 'New commission structure',
      questions: [
        {
          id: 'What are the key terms to be aware of?',
          question: 'What are the key terms to be aware of?',
          answer: (
            <>
              <p>
                <b>Withdrawal Fee:</b>
                This is the fee paid by your customer on a Flexi-Loan. This is
                different to, and will often not be the same as, your commission
                rate. You can adjust the withdrawal fee on a case by case basis
                in your Portal, which will directly impact the commission you
                earn.
              </p>

              <p className={styles.paragraph}>
                <b>Preferred Commission:</b>
                This is your chance to tell us the commission rate you’d prefer
                your offers to start at. We will take this into account when
                configuring the starting point for your offers, but don’t
                guarantee it.
              </p>

              <p className={styles.paragraph}>
                <b>Commission:</b>
                <ul>
                  <li>
                    Initial Commission is what we’ll pay you for all drawdowns
                    the customer makes in the first 30 days following their
                    initial drawdown.
                  </li>
                  <li>
                    Repeat Commission is what we’ll pay you for all drawdowns
                    the customer makes from day 31 to day 365 following their
                    initial drawdown.
                  </li>
                </ul>
              </p>

              <p className={styles.paragraph}>
                <b>Reintroduction:</b>
                You’re able to reintroduce an existing iwoca customer back to us
                when they have a £0 balance. If we fund them, we’ll pay you the
                same way we would treat a new customer, so you’ll earn both
                Initial Commission and Repeat Commission as set out below.
              </p>
            </>
          ),
          showInDE: false,
        },
        {
          id: 'Why has iwoca introduced a new commissions system?',
          question: 'Why has iwoca introduced a new commissions system?',
          answer: (
            <p>
              We want to give you, our introducers, more control and flexibility
              over the commission you earn from referring customers to us. We
              also want to give you a simpler system that works the same way for
              all of our Flexi-Loans.
            </p>
          ),
          showInDE: false,
        },
        {
          id: 'How do I know how much commission I’ll be paid on the deals I place with iwoca?',
          question:
            'How do I know how much commission I’ll be paid on the deals I place with iwoca?',
          answer: (
            <p>
              You'll see the rate of commission we’ll pay you for initial and
              repeat fundings on the Portal at the point where you approve the
              offer. You’ll also see the amount we plan to pay you for the
              initial funding, but if the customer draws down less than their
              maximum offer the final amount we pay you will reduce in
              proportion.
            </p>
          ),
          showInDE: false,
        },
        {
          id: 'How does iwoca set the Initial Commission rate when presenting the first offer to you?',
          question:
            'How does iwoca set the Initial Commission rate when presenting the first offer to you?',
          answer: (
            <>
              <p>
                We’re giving you the chance to tell us what rate of commission
                you’d ideally like to earn on all of your iwoca deals. We call
                this your Preferred Commission (and ‘Preferred Initial
                Commission Percentage’ in the Introducer Agreement you sign).
                Whenever we present an offer to you, we take into account your
                preferred rate.
              </p>

              <p className={styles.paragraph}>
                We can’t guarantee that we’ll be able to give you your Preferred
                Commission every time, but we generally aim to get as close as
                possible. For example, we may need to adjust the commission on a
                loan in order to stay within a maximum customer APR.
              </p>
            </>
          ),
          showInDE: false,
        },
        {
          id: 'What’s the maximum and minimum Preferred Commission I can set?',
          question:
            'What’s the maximum and minimum Preferred Commission I can set?',
          answer: (
            <p>
              You can set your Preferred Commission to a maximum of 10% and a
              minimum of 0%.
            </p>
          ),
          showInDE: false,
        },
        {
          id: 'How do I set my Preferred Commission?',
          question: 'How do I set my Preferred Commission?',
          answer: (
            <>
              <p>
                Your Preferred Commission is the commission rate you’d ideally
                like to receive on all your approved offers.
              </p>

              <p className={styles.paragraph}>
                An authorised signatory of your company can set your Preferred
                Commission with us at any time by completing the Google form{' '}
                <Link
                  to="https://forms.gle/gUJdjYd6FLCBynDcA"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  here
                </Link>{' '}
                or by contacting the iwoca team.
              </p>
            </>
          ),
          showInDE: false,
        },
        {
          id: 'Can I change the Withdrawal Fee for a specific customer? If so, what is the maximum and minimum Withdrawal Fee I can set?',
          question:
            'Can I change the Withdrawal Fee for a specific customer? If so, what is the maximum and minimum Withdrawal Fee I can set?',
          answer: (
            <>
              <p>
                Yes, you can adjust the customer Withdrawal Fee separately for
                each individual customer offer in your Portal. The drop-down box
                in your Portal will show you the maximum and minimum for the
                specific offer.
              </p>

              <p className={styles.paragraph}>
                You can adjust the customer Withdrawal Fee within ranges for
                different Flexi-Loan durations, as follows:
                <ul>
                  <li>Up to 12 months: 0 - 5%</li>
                  <li>15 months: 0% only</li>
                  <li>24 months: 0 - 10%</li>
                  <li>Over 24 months: 1 - 10%</li>
                </ul>
              </p>

              <p className={styles.paragraph}>
                The Withdrawal Fee is the fee paid on a Flexi-Loan by the
                customer. This is different to, and will often not be the same
                as, the Initial or Repeat Commission rate. If you adjust the
                customer Withdrawal Fee this will directly affect the Commission
                you earn, as explained below.
              </p>
            </>
          ),
          showInDE: false,
        },
        {
          id: 'How does changing the customer Withdrawal Fee affect the Commission I earn?',
          question:
            'How does changing the customer Withdrawal Fee affect the Commission I earn?',
          answer: (
            <>
              <p>
                If you adjust the customer Withdrawal Fee on your Portal this
                will directly affect the amount of commission you earn. If you
                increase the Withdrawal Fee by 1% you’ll earn 1% more Initial
                Commission. If you reduce the Withdrawal Fee by 1% you’ll earn
                1% less Initial Commission.
              </p>

              <p className={styles.paragraph}>
                Repeat Commission is also calculated based on the Withdrawal
                Fee, as explained below.
              </p>
            </>
          ),
          showInDE: false,
        },
        {
          id: 'What level of Withdrawal Fees does iwoca usually charge customers who apply directly?',
          question:
            'What level of Withdrawal Fees does iwoca usually charge customers who apply directly?',
          answer: (
            <p className={styles.paragraph}>
              As of now, our direct fees are:
              <ul>
                <li>No fee on our 12 month Flexi-Loans</li>
                <li>No fee on our 15 month Flexi-Loans</li>
                <li>5% on our 24 month Flexi-Loans</li>
                <li>6% on our 60 month Flexi-Loans</li>
              </ul>
            </p>
          ),
          showInDE: false,
        },
        {
          id: 'Is the iwoca Portal the only place where I can adjust customer Withdrawal Fees?',
          question:
            'Is the iwoca Portal the only place where I can adjust customer Withdrawal Fees?',
          answer: (
            <p>
              Yes, you can only adjust customer Withdrawal Fees in your Portal.
              We cannot process change requests by phone or email.
            </p>
          ),
          showInDE: false,
        },
        {
          id: 'How does iwoca set the Repeat Commission rate?',
          question: 'How does iwoca set the Repeat Commission rate?',
          answer: (
            <p>
              iwoca sets the Repeat Commission rate at half of the Withdrawal
              Fee on first drawdown. For example, if the Withdrawal Fee is 5%
              then the Repeat Commission rate is 2.5%.
            </p>
          ),
          showInDE: false,
        },
        {
          id: 'Do I still earn Repeat Commission if the customer takes a ‘direct’ repeat  funding, i.e. directly with iwoca and without my involvement? ',
          question:
            'Do I still earn Repeat Commission if the customer takes a ‘direct’ repeat  funding, i.e. directly with iwoca and without my involvement? ',
          answer: (
            <p>
              If a Withdrawal Fee applies to a customer's first drawdown, you'll
              earn Repeat Commission on any ‘direct’ repeat fundings they make
              more than 30 days after their first drawdown, for up to 12 months.
              This does not apply if they return to iwoca via another
              introducer. You’ll earn Initial Commission if you reintroduce the
              customer instead of them coming ‘direct’.
            </p>
          ),
          showInDE: false,
        },
        {
          id: 'If I bring back to iwoca a customer who’s had an iwoca loan in the past, will I only get paid at the Repeat Commission rate?',
          question:
            'If I bring back to iwoca a customer who’s had an iwoca loan in the past, will I only get paid at the Repeat Commission rate?',
          answer: (
            <>
              <p>
                If the customer fully repaid their previous loan and you bring
                the customer back, you’ll get full Initial Commission for this
                ‘reintroduction’.
              </p>

              <p className={styles.paragraph}>
                Following your reintroduction, if the customer draws down
                themselves directly with iwoca, you’ll get Repeat Commission for
                any fundings between day 31 and 365 following their initial
                drawdown.
              </p>
            </>
          ),
          showInDE: false,
        },
      ],
    });
  }

  if (isUK) {
    data.push(
      {
        id: 'commission-disclosure',
        icon: <IconEyeFilled />,
        topic: 'Commission disclosure',
        questions: [
          {
            id: 'At which point in the customer journey do you expect introducers to disclose commission?',
            question:
              'At which point in the customer journey do you expect introducers to disclose commission?',
            answer: (
              <p>
                As set out in our agreement with you, you’re obligated to tell
                customers (in writing) about any commission, fees or payments
                you may receive from us before they make a drawdown.
              </p>
            ),
            showInDE: false,
          },
          {
            id: 'How will iwoca disclose commission to customers?',
            question: 'How will iwoca disclose commission to customers?',
            answer: (
              <>
                <p>
                  Customers will need to acknowledge commission payments using a
                  tick box that is displayed in their account pages during the
                  drawdown steps. We’ll disclose the amount and commission
                  calculation.
                </p>
                <p className={styles.paragraph}>
                  We won’t disclose commission in the customer’s loan agreement
                  or in any other document.
                </p>
              </>
            ),
            showInDE: false,
          },
          {
            id: 'What happens if the customer chooses not to consent to the proposed commission?',
            question:
              'What happens if the customer chooses not to consent to the proposed commission?',
            answer: (
              <p>
                If the customer does not consent to the commission, they won’t
                be able to draw down the funds. iwoca will let the introducer
                know and ask them to resolve the matter directly with their
                customer.
              </p>
            ),
            showInDE: false,
          },
        ],
      },
      {
        id: 'early-repayments',
        icon: <IconPound />,
        topic: 'Early repayments',
        questions: [
          {
            id: 'Can my customer make early repayments?',
            question: 'Can my customer make early repayments?',
            answer: (
              <p>
                Yes, customers can make early repayments at any time, completely
                free of charge.
              </p>
            ),
            showInDE: false,
          },
          {
            id: 'How can my customer make early repayments?',
            question: 'How can my customer make early repayments?',
            answer: (
              <p>
                Early repayments can be made through the customer portal. Simply
                click on the “Repay early for free” option and follow the steps
                provided.
              </p>
            ),
            showInDE: false,
          },
          {
            id: `How do early repayments affect my customers' repayment schedule?`,
            question: `How do early repayments affect my customers' repayment schedule?`,
            answer: (
              <p>
                Any overpayment is applied to upcoming repayments, effectively
                covering the next scheduled payments. This creates a temporary
                “payment holiday” for the customer.
                <p className={styles.paragraph}>
                  Example:
                  <ul>
                    <li>Funded amount: £10,000</li>
                    <li> Term: 12 months</li>
                    <li> Interest rate: 2.9%</li>
                    <li>Monthly repayment: £1,000.64</li>
                    <li> First loan repayment due on: 01/01/25</li>
                    <li>
                      If the customer makes a £5,000 early repayment on
                      01/01/25, this results in a repayment break until June
                      2025, with repayments resuming on 1/07/25.
                    </li>
                  </ul>
                </p>
              </p>
            ),
            showInDE: false,
          },
          {
            id: 'Can my customers make additional early repayments during the payment holiday?',
            question:
              'Can my customers make additional early repayments during the payment holiday?',
            answer: (
              <p>
                Yes, customers can make additional early repayments at any time
                during their payment holiday by using their portal.
              </p>
            ),
            showInDE: false,
          },
          {
            id: 'How does early repayments affect the interest accrued on the loan and payable by the customer?',
            question:
              'How does early repayments affect the interest accrued on the loan and payable by the customer?',
            answer: (
              <p>
                The interest is calculated on the remaining balance of the loan.
                By paying off the loan earlier than scheduled, the customer
                decreases the principal balance quicker, which in turn reduces
                the amount of interest accrued on the loan. This can result in
                significant savings, depending on the loan's interest rate and
                term.
              </p>
            ),
            showInDE: false,
          },
          {
            id: `If the customer's loan includes a withdrawal fee, will repaying early reduce the fee amount?`,
            question: `If the customer's loan includes a withdrawal fee, will repaying early reduce the fee amount?`,
            answer: (
              <p>
                No, the full fee amount agreed at the draw down stage is payable
                by the customer even if they repay the loan early.{' '}
              </p>
            ),
            showInDE: false,
          },
          {
            id: `Will my customers' early repayments affect my commission?`,
            question: `Will my customers' early repayments affect my commission?`,
            answer: (
              <p>
                Your commission typically remains unaffected; however, we
                reserve the right to clawback commission in cases of detected
                misconduct.
              </p>
            ),
            showInDE: false,
          },
        ],
      },
    );

    // Add 'Our offers' section to array after 'Our products'
    const ourProductsSectionIndex = data.findIndex(
      (section) => section.id === 'our-products',
    );

    if (
      ourProductsSectionIndex !== -1 &&
      splitTests?.first_introduced_feeless_longer_durations_feb26
    ) {
      data.splice(ourProductsSectionIndex + 1, 0, ourOffersSection);
    }
  }

  return data;
};

const ourOffersSection = {
  id: 'our-offers',
  icon: <IconPartyPopper />,
  topic: 'Our offers',
  questions: [
    {
      id: 'Why are you implementing this new change?',
      question: 'Why are you implementing this new change?',
      answer: (
        <p>
          To stay competitive in the market, and support more of your customers
          who wish to minimise their upfront costs over 24-60 month durations.
        </p>
      ),
      showInDE: false,
    },
    {
      id: `Who's eligible for these new zero withdrawal fee offers?`,
      question: `Who's eligible for these new zero withdrawal fee offers?`,
      answer: (
        <p>
          This will initially be launched on a trial basis to all introducers,
          and available to <strong>new customers only</strong>. However, its
          availability will be determined by customer eligibility rather than on
          request.
        </p>
      ),
      showInDE: false,
    },

    {
      id: 'Do these new zero withdrawal fee offers apply to re-introductions?',
      question:
        'Do these new zero withdrawal fee offers apply to re-introductions?',
      answer: (
        <p>
          No. This change is exclusively for <strong>new customers</strong> who
          have not had a prior loan with us or have an ongoing loan now.
          Re-introductions follow the standard commission structures.
        </p>
      ),
      showInDE: false,
    },
    {
      id: 'Can I request a no fee option for my customer?',
      question: 'Can I request a no fee option for my customer?',
      answer: (
        <p>
          Not for longer durations, iwoca will offer these at our discretion to
          new customers only. However, the 12 month option will always be
          available with no fee.
        </p>
      ),
      showInDE: false,
    },

    {
      id: 'How do I set my Preferred Rate of Initial Commission?',
      question: 'How do I set my Preferred Rate of Initial Commission?',
      answer: (
        <p>
          An authorised signatory from your firm will need to complete{' '}
          <Link
            to="https://forms.gle/qS4bqFUirxFJoPDy8"
            target="_blank"
            rel="noopener noreferrer"
          >
            THIS
          </Link>{' '}
          google form or confirm your Preferred Rate in writing by emailing{' '}
          <a href="mailto:brokers@iwoca.co.uk">brokers@iwoca.co.uk</a>. This can
          be set within our range 0-10% and we will aim to send offers as close
          to this preferred rate as possible.
        </p>
      ),
      showInDE: false,
    },
    {
      id: 'Does selecting the maximum 10% Preferred Rate of Initial Commission make the loan more expensive for my customer?',
      question:
        'Does selecting the maximum 10% Preferred Rate of Initial Commission make the loan more expensive for my customer?',
      answer: (
        <p>
          The non-fee commission will be taken out of our margin in order to
          make these offers more competitive.
        </p>
      ),
      showInDE: false,
    },
    {
      id: 'Can I still adjust my commission?',
      question: 'Can I still adjust my commission?',
      answer: (
        <>
          <p>
            For existing customers, and some new customers where we're able to
            offer this ability.
          </p>
          <p>
            However, we do recommend that you set your Preferred Rate of Initial
            Commission at a rate that you're happy to accept across all the
            customers you send us.
          </p>
        </>
      ),
      showInDE: false,
    },
    {
      id: 'How can I adjust my commission for a specific offer?',
      question: 'How can I adjust my commission for a specific offer?',
      answer: (
        <p>
          This will still be via the broker Portal. When you log into your
          Portal you'll see all the offers available to each customer. If the
          offer can be adjusted using the grid next to the Withdrawal Fee and
          you'll see how this affects your Initial and Repeat Commission.
        </p>
      ),
      showInDE: false,
    },
    {
      id: 'What is the maximum and minimum Withdrawal Fee the customer would pay?',
      question:
        'What is the maximum and minimum Withdrawal Fee the customer would pay?',
      answer: (
        <>
          <p>
            The customer Withdrawal Fee within ranges for different Flexi-Loan
            durations, as follows:
          </p>
          <ul>
            <li>12 months: 0-5%</li>
            <li>15 months: 0% only (not adjustable)</li>
            <li>24 months: 0-10%</li>
            <li>36/48/60 months: 0-10%</li>
          </ul>
          <p>Unless APR-capped. We do not exceed 99% APR</p>
        </>
      ),
      showInDE: false,
    },
    {
      id: 'Will the Commission rate and Customer Withdrawal fee be equivalent?',
      question:
        'Will the Commission rate and Customer Withdrawal fee be equivalent?',
      answer: (
        <p>
          Not always, the Withdrawal Fee and Initial Commission are two separate
          rates. While they are mutually exclusive, they may sometimes be the
          same number, but not by design or principle. They will often be
          different.
        </p>
      ),
      showInDE: false,
    },
    {
      id: 'What will happen to existing and previous customers?',
      question: 'What will happen to existing and previous customers?',
      answer: (
        <p>
          They will be subject to our current terms, you will be able to adjust
          the Withdrawal Fee up and down on offer durations in the portal and
          see how this affects your Initial Commission.
        </p>
      ),
      showInDE: false,
    },
  ],
};
